import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { EmptyQuotationFormView } from '@euler/app/flows/task/pages/quotation/components/EmptyQuotationFormView';
import { ConstructionItemEditorBottomSheet } from '@euler/app/flows/task/pages/quotation/components/quotation-form/ConstructionItemEditor';
import {
  ConstructionItemBeingEdited,
  MaterialBeingEdited,
  QuotationFormContext,
} from '@euler/app/flows/task/pages/quotation/components/quotation-form/Context';
import { QuotationFormFooter } from '@euler/app/flows/task/pages/quotation/components/quotation-form/Footer';
import { MediaEditorBottomSheet } from '@euler/app/flows/task/pages/quotation/components/quotation-form/MaterialEditor';
import { QuotationSubjectRelatedIssuesBottomSheet } from '@euler/app/flows/task/pages/quotation/components/quotation-form/RelatedIssuesBottomSheet';
import { WiredQuotationFormSubjectPanel } from '@euler/app/flows/task/pages/quotation/components/quotation-form/SubjectPanel';
import { useAmendQuotationForm } from '@euler/app/flows/task/pages/quotation/functions/useAmendQuotationForm';
import { usePrintQuotationForm } from '@euler/app/flows/task/pages/quotation/functions/usePrintQuotationForm';
import { useQuotationNavigation } from '@euler/app/flows/task/pages/quotation/functions/useQuotationNavigation';
import { useReconciliateQuotationForm } from '@euler/app/flows/task/pages/quotation/functions/useReconciliateQuotationForm';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useAppLoading } from '@euler/components/loading/Provider';
import { isDebugEnabled } from '@euler/functions/debug';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { InspectionTaskStatus } from '@euler/model/enum';
import { QuotationSubjectStaged } from '@euler/model/task-detail';
import { SafeHaptics, delayed, makeDebug, onErrorIgnore } from '@euler/utils';
import { array_move } from '@euler/utils/array';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { FlatList, ListRenderItem } from 'react-native';
import Toast from 'react-native-root-toast';
import { BehaviorSubject } from 'rxjs';
import invariant from 'tiny-invariant';

const keyExtractor = (subject: QuotationSubjectStaged) => subject.name;

const debug = makeDebug('taskmanager:quotation', isDebugEnabled('quotation'));

export const QuotationFormPage = wrapNavigatorScreen(
  ({
    editable = true,
  }: {
    editable?: boolean;
    animationEnabled?: boolean;
    animationTypeForReplace?: 'pop' | 'push';
  }) => {
    useToggleAutomaticKeyboard(true);

    const loading = useAppLoading();
    const alert = useAlert();
    const confirm = useConfirm();
    const navigation = useQuotationNavigation();
    const reconciliateQuotationForm = useReconciliateQuotationForm();
    const onRequestAmend = useAmendQuotationForm();

    const { rpx } = useResponsiveContext();
    const { taskManager, detail } = useTaskContext();
    const [{ noAlertBeforeNavigateAwayQuotationForm }, updateSettings] =
      useUserLocalSettings();

    const quotationManager = taskManager.quotationManager;

    const [staged] = useBehaviorSubject(quotationManager.staged$);
    const [dirty] = useBehaviorSubject(quotationManager.dirty$);
    const subjects = useMemo(
      () =>
        editable ? staged.subjects : staged.subjects.filter(x => x.included),
      [editable, staged.subjects],
    );

    const subjectWhoseIssuesBeingEdited$ = useCreation(
      () => new BehaviorSubject<QuotationSubjectStaged | undefined>(undefined),
      [],
    );

    const materialBeingEdited$ = useCreation(
      () => new BehaviorSubject<MaterialBeingEdited | undefined>(undefined),
      [],
    );

    const constructionItemBeingEdited$ = useCreation(
      () =>
        new BehaviorSubject<ConstructionItemBeingEdited | undefined>(undefined),
      [],
    );

    const onDone = useCallback(
      (selection: string[]) => {
        reconciliateQuotationForm(selection);
        navigation.goBack();
      },
      [navigation, reconciliateQuotationForm],
    );

    const onAdd = useCallback(() => {
      navigation.navigate('_catalog', {
        onDone,
      });
    }, [navigation, onDone]);

    const onMoveUp = useCallback(
      (_subject: QuotationSubjectStaged, index: number) => {
        invariant(index > 0);
        quotationManager.produce(draft => {
          array_move(draft.subjects, index, index - 1);
        });
      },
      [quotationManager],
    );

    const onMoveDown = useCallback(
      (_subject: QuotationSubjectStaged, index: number) => {
        invariant(index >= 0 && index < subjects.length - 1);
        quotationManager.produce(draft => {
          array_move(draft.subjects, index, index + 1);
        });
      },
      [subjects, quotationManager],
    );

    const onSubjectChange = useCallback(
      (subject: QuotationSubjectStaged, index: number) => {
        quotationManager.produce(draft => {
          if (index >= 0) {
            if (draft.subjects[index].name !== subject.name) {
              // name changed, make sure the new name does not
              // conflict with existing ones
              if (
                draft.subjects.some(
                  (x, i) => i !== index && x.name === subject.name,
                )
              ) {
                Toast.show(`报价项目名称"${subject.name}"重复!`, {
                  duration: 500,
                  position: Toast.positions.CENTER,
                });
                SafeHaptics.impact();
                return;
              }
            }
            draft.subjects[index] = subject;
          }
          if (!draft.subjects.filter(x => x.included).length) {
            draft.additionalTotalFeeDiscount = undefined;
          }
        });
      },
      [quotationManager],
    );

    const onRemoveSubject = useCallback(
      (_subject: QuotationSubjectStaged, index: number) => {
        quotationManager.produce(draft => {
          if (index >= 0) {
            draft.subjects.splice(index, 1);
          }
        });
      },
      [quotationManager],
    );

    const onCheckNamePermitted = useCallback(
      (_subject: QuotationSubjectStaged, index: number, name: string) => {
        if (subjects[index].name === name) {
          return true;
        }
        return !subjects.some((x, i) => i !== index && x.name === name);
      },
      [subjects],
    );

    const count = subjects.length;

    const renderItem: ListRenderItem<QuotationSubjectStaged> = useCallback(
      ({ item, index }) => {
        return (
          <WiredQuotationFormSubjectPanel
            subject={item}
            index={index}
            editable={editable}
            canMoveUp={index > 0}
            canMoveDown={index < count - 1}
            onChange={onSubjectChange}
            onRemove={onRemoveSubject}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onCheckNamePermitted={onCheckNamePermitted}
            css={`
              margin-top: ${rpx(10)}px;
            `}
          />
        );
      },
      [
        count,
        editable,
        onCheckNamePermitted,
        onMoveDown,
        onMoveUp,
        onRemoveSubject,
        onSubjectChange,
        rpx,
      ],
    );

    const applyCached = usePersistFn(async () => {
      if (!editable) {
        debug('discard cache in non-editable mode');
        await quotationManager.discardCache();
        return;
      }
      const cached = await quotationManager.loadCached();
      if (cached == null) {
        debug('no cached form data found');
        return;
      }

      if (detail.quotation.quotationForm == null) {
        debug('form not saved yet, load cached data directly w/o asking');
        quotationManager.applyCached(cached);
        return;
      }

      // only apply cached contents to the from if the version matches
      if (cached.version !== detail.quotation.quotationForm.version) {
        await quotationManager.discardCache();
        debug('cached data version mismatch, discard cache and ignore');
        return;
      }

      if (await confirm({ title: '是否恢复上次编辑的内容?' })) {
        debug('user confirmed to apply cached data, apply cached data');
        quotationManager.applyCached(cached);
      } else {
        debug('user declined to apply cached data, discard cache');
        await quotationManager.discardCache();
      }
    });

    const onPrint = usePrintQuotationForm(detail.basicInfo.orderNo, {
      type: 'quotation-form',
      showPrice: true,
    });

    useEffect(() => {
      if (!editable) return;

      navigation.setOptions({
        headerRight: () => (
          <Center.Touchable onPress={onAdd} mr={rpx(15)}>
            <Icon name="plus" size={rpx(20)} color="#000" />
          </Center.Touchable>
        ),
      });
    }, [editable, navigation, onAdd, rpx]);

    const onDiscountChange = useCallback(
      (discount: number) => {
        quotationManager.produce(draft => {
          draft.additionalTotalFeeDiscount = discount;
        });
      },
      [quotationManager],
    );

    const onSave = useCallback(async () => {
      await loading.showWithAction(
        async () => {
          await quotationManager.save({ confirmed: false });
          delayed(() => {
            loading.show({
              type: 'success',
            });
          }, 500);
        },
        { delay: 0 },
      );
    }, [loading, quotationManager]);

    const onConfirm = useCallback(async () => {
      if (
        await confirm({
          destructive: true,
          title: '确认报价',
          message:
            '您即将提交并确认当前报价单，确认报价单后系统将生成维修结算单，是否继续? ',
        })
      ) {
        await loading.showWithAction(async () => {
          await quotationManager.save({ confirmed: true });

          delayed(() => {
            navigation.replace('_settlementForm', {
              animationTypeForReplace: 'push',
            });
            loading.show({
              type: 'success',
            });
          }, 500);
        });
      }
    }, [confirm, loading, navigation, quotationManager]);

    useEffect(() => {
      applyCached()
        .then(() => {
          if (editable) {
            quotationManager.startObserveChanges();
          }
        })
        .catch(onErrorIgnore);
    }, [applyCached, editable, quotationManager]);

    useEffect(() => {
      const removeListener = navigation.addListener('beforeRemove', e => {
        if (!dirty || noAlertBeforeNavigateAwayQuotationForm) {
          return;
        }

        e.preventDefault();

        void alert({
          title: '提示',
          message: [
            '当前报价单已修改，您所录入的报价信息已保存在本地缓存中, ',
            '返回后不会丢失，我们建议您及时保存报价单数据，是否立即保存？',
          ].join(''),
          buttons: [
            {
              text: '返回并放弃当前修改',
              style: 'destructive',
              onPress: async () => {
                await quotationManager
                  .resetAndClearCache()
                  .catch(onErrorIgnore);
                navigation.dispatch(e.data.action);
              },
            },
            {
              text: '保存修改并返回',
              style: 'default',
              onPress: async () => {
                setTimeout(async () => {
                  await onSave();
                  delayed(() => {
                    navigation.dispatch(e.data.action);
                  }, 150);
                }, 0);
              },
            },
            {
              text: '直接返回',
              style: 'default',
              onPress: () => {
                navigation.dispatch(e.data.action);
              },
            },
            {
              text: '直接返回并不再提示',
              onPress: () => {
                updateSettings({
                  noAlertBeforeNavigateAwayQuotationForm: true,
                });
                navigation.dispatch(e.data.action);
              },
            },
            {
              text: '取消',
              style: 'cancel',
            },
          ],
        });
      });
      return removeListener;
    }, [
      alert,
      dirty,
      navigation,
      noAlertBeforeNavigateAwayQuotationForm,
      onSave,
      quotationManager,
      updateSettings,
    ]);

    const updateNavigationTitle = usePersistFn((showDirty: boolean) => {
      navigation.setOptions({
        title: showDirty
          ? `维修报价单\n!color[${StatusColors.Warn}] 未保存`
          : '维修报价单',
      });
    });

    useEffect(() => {
      updateNavigationTitle(dirty);
    }, [dirty, updateNavigationTitle]);

    if (!subjects.length) {
      return <EmptyQuotationFormView onAdd={onAdd} />;
    }

    return (
      <QuotationFormContext.Provider
        value={{
          editable,
          subjectWhoseIssuesBeingEdited$,
          constructionItemBeingEdited$,
          materialBeingEdited$,
        }}
      >
        <FlatList
          data={subjects}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          keyboardShouldPersistTaps="handled"
          initialNumToRender={20}
          contentContainerStyle={{
            paddingBottom: rpx(15),
            flexGrow: 1,
          }}
        />
        <Suspense fallback={null}>
          <QuotationFormFooter
            readOnly={
              detail.basicInfo.status === InspectionTaskStatus.Finished ||
              detail.basicInfo.status === InspectionTaskStatus.Cancelled
            }
            dirty={dirty}
            editable={editable}
            form={staged}
            onDiscountChange={onDiscountChange}
            onSave={onSave}
            onConfirm={onConfirm}
            onRequestAmend={onRequestAmend}
            onPrint={onPrint}
          />
          {editable && <ConstructionItemEditorBottomSheet />}
          {editable && <MediaEditorBottomSheet />}
          {editable && <QuotationSubjectRelatedIssuesBottomSheet />}
        </Suspense>
      </QuotationFormContext.Provider>
    );
  },
  params => ({
    title: '维修报价单',
    animationEnabled: params.route.params?.animationEnabled,
    animationTypeForReplace: params.route.params?.animationTypeForReplace,
  }),
);
